/**
 * useDashboardModule — deterministic REQ-090 state machine for a placeholder
 * admin module (ODE-P01-T10).
 *
 * Flow: `pending` (initial, data source deferred) → user activates via
 * `load(purposeCode)` → `loading` → terminal state derived from the guarded
 * API outcome (200 → empty, 401 → unauthorized, 403 → blocked, else error).
 * No real data wiring — the guarded endpoint returns an empty placeholder
 * payload; data flows are deferred to later tasks.
 *
 * Traces to: ODE-PRD-REQ-043..048, ODE-PRD-REQ-090.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client.js';
import type { ModuleDefinition } from '../rbac/catalog.js';
import type { ModuleState } from './state.js';

interface PendingRequest {
  readonly attempt: number;
  readonly purposeCode: string;
}

export function useDashboardModule(module: ModuleDefinition, token: string | null) {
  const [state, setState] = useState<ModuleState>({ kind: 'pending' });
  const [request, setRequest] = useState<PendingRequest | null>(null);
  const attemptRef = useRef(0);

  /** Activates the module fetch with the current purpose code (if any). */
  const load = useCallback((purposeCode: string) => {
    attemptRef.current += 1;
    setRequest({ attempt: attemptRef.current, purposeCode });
  }, []);

  useEffect(() => {
    if (request === null) return;
    if (token === null) {
      setState({ kind: 'unauthorized' });
      return;
    }
    let cancelled = false;
    setState({ kind: 'loading' });
    api
      .fetchModule(module.id, token, request.purposeCode)
      .then((result) => {
        if (cancelled) return;
        switch (result.status) {
          case 200:
            setState({ kind: 'empty', payload: result.payload });
            break;
          case 401:
            setState({ kind: 'unauthorized' });
            break;
          case 403:
            setState({ kind: 'blocked', message: result.message ?? 'Forbidden' });
            break;
          default:
            setState({ kind: 'error', message: result.message ?? `HTTP ${result.status}` });
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: error instanceof Error ? error.message : 'Network error',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [module.id, token, request]);

  return { state, load };
}